import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Fab,
    Paper,
    Typography,
    TextField,
    IconButton,
    List,
    ListItem,
    Avatar,
    CircularProgress,
    Slide,
    Zoom,
    Divider,
    Tooltip,
    Button
} from '@mui/material';
import {
    SmartToy,
    Close,
    Send,
    DeleteOutline,
    KeyboardArrowDown
} from '@mui/icons-material';
import { aiChatService, ChatMessage } from '../services/aiChatService';

export const AIChatWidget: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [visibleCount, setVisibleCount] = useState(10);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // --- PERSISTENCE LOGIC START ---
    const getStorageKey = () => {
        try {
            const userStr = sessionStorage.getItem('user');
            if (userStr) {
                const user = JSON.parse(userStr);
                return user && user.id ? `chat_history_${user.id}` : 'chat_history_guest';
            }
        } catch (e) {
            console.error('Error parsing user from sessionStorage', e);
        }
        return 'chat_history_guest';
    };

    const [messages, setMessages] = useState<ChatMessage[]>(() => {
        try {
            const key = getStorageKey();
            const saved = localStorage.getItem(key);
            return saved ? JSON.parse(saved) : [];
        } catch (e) {
            return [];
        }
    });

    useEffect(() => {
        try {
            const key = getStorageKey();
            localStorage.setItem(key, JSON.stringify(messages));
        } catch (e) {
            console.error('Error saving chat history', e);
        }
        // Small delay to ensure DOM has updated
        const timer = setTimeout(scrollToBottom, 50);
        return () => clearTimeout(timer);
    }, [messages]);
    // --- PERSISTENCE LOGIC END ---

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const toggleChat = () => setIsOpen(!isOpen);

    const loadMore = () => {
        setVisibleCount(prev => prev + 10);
    };

    const handleSend = async () => {
        if (!message.trim() || isLoading) return;

        const userMessage: ChatMessage = {
            role: 'user',
            content: message,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };

        setMessages(prev => [...prev, userMessage]);
        setMessage('');
        setIsLoading(true);

        try {
            const reply = await aiChatService.chat(message, messages);
            const botMessage: ChatMessage = {
                role: 'assistant',
                content: reply,
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            };
            setMessages(prev => [...prev, botMessage]);
        } catch (error) {
            const errorMessage: ChatMessage = {
                role: 'assistant',
                content: 'Xin lỗi, tôi gặp chút trục trặc khi kết nối. Bạn có thể thử lại sau nhé!',
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const clearChat = () => {
        if (window.confirm('Bạn có muốn xóa lịch sử trò chuyện này không?')) {
            setMessages([]);
            // Also revert storage immediately (handled by useEffect, but good to be explicit/safe or just let useEffect handle it)
            try {
                const key = getStorageKey();
                localStorage.removeItem(key);
            } catch (e) { }
        }
    };

    return (
        <Box sx={{ position: 'fixed', bottom: 30, right: 30, zIndex: 1100 }}>
            <Zoom in={!isOpen}>
                <Fab
                    color="primary"
                    aria-label="chat"
                    onClick={toggleChat}
                    sx={{
                        width: 60,
                        height: 60,
                        boxShadow: '0 8px 32px rgba(25, 118, 210, 0.4)',
                        transition: 'transform 0.3s ease',
                        '&:hover': { transform: 'scale(1.1) rotate(5deg)' }
                    }}
                >
                    <SmartToy fontSize="large" />
                </Fab>
            </Zoom>

            <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
                <Paper
                    elevation={12}
                    sx={{
                        width: { xs: '90vw', sm: 400 },
                        height: 600,
                        maxHeight: '80vh',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 3,
                        overflow: 'hidden',
                        boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
                        border: '1px solid rgba(0,0,0,0.05)'
                    }}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            p: 2,
                            bgcolor: 'primary.main',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar
                                sx={{
                                    bgcolor: 'white',
                                    color: 'primary.main',
                                    width: 40,
                                    height: 40,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                }}
                            >
                                <SmartToy />
                            </Avatar>
                            <Box>
                                <Typography variant="h6" sx={{ lineHeight: 1.2, fontWeight: 600 }}>
                                    Trợ Lý Thư Viện
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            bgcolor: '#4caf50', // Green for online
                                            boxShadow: '0 0 4px #4caf50'
                                        }}
                                    />
                                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                        Online
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                        <Box>
                            <Tooltip title="Xóa lịch sử">
                                <IconButton color="inherit" onClick={clearChat} size="small">
                                    <DeleteOutline />
                                </IconButton>
                            </Tooltip>
                            <IconButton color="inherit" onClick={toggleChat} size="small">
                                <KeyboardArrowDown />
                            </IconButton>
                        </Box>
                    </Box>

                    <Divider />

                    {/* Messages Area */}
                    <Box
                        sx={{
                            flexGrow: 1,
                            p: 2,
                            overflowY: 'auto',
                            bgcolor: '#f8f9fa',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2
                        }}
                    >
                        {messages.length > visibleCount && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                                <Button
                                    size="small"
                                    onClick={loadMore}
                                    sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
                                >
                                    Xem thêm hội thoại cũ
                                </Button>
                            </Box>
                        )}

                        {messages.length === 0 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                    opacity: 0.5,
                                    textAlign: 'center',
                                    p: 3
                                }}
                            >
                                <SmartToy sx={{ fontSize: 48, mb: 1 }} />
                                <Typography variant="body2">
                                    Chào bạn! Tôi có thể giúp bạn tìm thông tin từ thư viện sách số.
                                </Typography>
                                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                    VD: "Trường mình có tài liệu nào về môn Toán lớp 10 không?"
                                </Typography>
                            </Box>
                        )}

                        {messages.slice(Math.max(0, messages.length - visibleCount)).map((msg, index) => (
                            <Box
                                key={index}
                                sx={{
                                    alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                                    maxWidth: '85%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start'
                                }}
                            >
                                <Paper
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        bgcolor: msg.role === 'user' ? 'primary.main' : 'white',
                                        color: msg.role === 'user' ? 'white' : 'text.primary',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                        borderBottomRightRadius: msg.role === 'user' ? 2 : 12,
                                        borderBottomLeftRadius: msg.role === 'user' ? 12 : 2
                                    }}
                                >
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {msg.content}
                                    </Typography>
                                </Paper>
                                <Typography variant="caption" sx={{ mt: 0.5, opacity: 0.5, fontSize: '0.65rem' }}>
                                    {msg.timestamp}
                                </Typography>
                            </Box>
                        ))}

                        {isLoading && (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 1 }}>
                                <CircularProgress size={16} thickness={5} />
                                <Typography variant="caption" color="text.secondary">Trợ lý đang suy nghĩ...</Typography>
                            </Box>
                        )}
                        <div ref={messagesEndRef} />
                    </Box>

                    <Divider />

                    {/* Input Area */}
                    <Box sx={{ p: 2, bgcolor: 'white' }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                                fullWidth
                                placeholder="Nhập câu hỏi tại đây..."
                                size="small"
                                multiline
                                maxRows={4}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                onKeyPress={handleKeyPress}
                                disabled={isLoading}
                                autoFocus
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        bgcolor: '#f1f3f4'
                                    }
                                }}
                            />
                            <IconButton
                                color="primary"
                                onClick={handleSend}
                                disabled={!message.trim() || isLoading}
                                sx={{
                                    bgcolor: message.trim() ? 'primary.main' : 'transparent',
                                    color: message.trim() ? 'white' : 'primary.main',
                                    '&:hover': {
                                        bgcolor: message.trim() ? 'primary.dark' : 'rgba(25, 118, 210, 0.04)'
                                    },
                                    width: 40,
                                    height: 40,
                                    transition: 'all 0.2s'
                                }}
                            >
                                {isLoading ? <CircularProgress size={20} color="inherit" /> : <Send />}
                            </IconButton>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center', fontSize: '0.65rem' }}>
                            Dữ liệu được bảo mật theo từng đơn vị trường học
                        </Typography>
                    </Box>
                </Paper>
            </Slide>
        </Box>
    );
};
